import React, { useEffect, useMemo, useState } from "react";
import API from "../../../services/api";
import SaveStudioAssetButton from "../SaveStudioAssetButton";
import FieldHint from "../FieldHint";
import useI18n from "../../../i18n/useI18n";

import MotionSurface from "../../../motion/studio/MotionSurface";
import ToolResultSkeleton from "../../loaders/ToolResultSkeleton";

import ResultCard from "../../ui/ResultCard";
import ActionRow from "../../ui/ActionRow";
import CopyButton from "../../ui/CopyButton";

import AIActionButton from "../../ui/buttons/AIActionButton";
import UtilityButton from "../../ui/buttons/UtilityButton";

const STUDIO_USAGE_REFRESH_KEY = "hookora_studio_usage_refresh";
const THEME_KEY = "hookora_theme";

const getPreferredLanguage = () => {
  if (typeof window === "undefined") return "en";
  return localStorage.getItem("hookora_language") || "en";
};

const notifyStudioUsageUpdated = () => {
  const value = String(Date.now());
  localStorage.setItem(STUDIO_USAGE_REFRESH_KEY, value);
  window.dispatchEvent(new CustomEvent("hookora:studio-usage-updated"));
};

const getScoreTone = (score) => {
  const safe = Number(score) || 0;
  if (safe >= 85) {
    return {
      bar: "#16a34a",
      pillBg: "rgba(22,163,74,0.14)",
      pillText: "#16a34a",
    };
  }
  if (safe >= 70) {
    return {
      bar: "#2563eb",
      pillBg: "rgba(37,99,235,0.14)",
      pillText: "#2563eb",
    };
  }
  return {
    bar: "#f59e0b",
    pillBg: "rgba(245,158,11,0.16)",
    pillText: "#b45309",
  };
};

const DescriptionTool = ({
  sharedTopic,
  setSharedTopic,
  sharedPlatform,
  setSharedPlatform,
}) => {
  const { t } = useI18n();

  const getStoredTheme = () => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem(THEME_KEY) || "light";
  };

  const [originalDescription, setOriginalDescription] = useState("");
  const [audience, setAudience] = useState("");
  const [goal, setGoal] = useState("");
  const [offer, setOffer] = useState("");
  const [painPoint, setPainPoint] = useState("");
  const [desiredOutcome, setDesiredOutcome] = useState("");
  const [angle, setAngle] = useState("");
  const [callToAction, setCallToAction] = useState("");
  const [keywords, setKeywords] = useState("");
  const [brandVoice, setBrandVoice] = useState("");
  const [avoidPhrases, setAvoidPhrases] = useState("");
  const [extraContext, setExtraContext] = useState("");

  const [rewrites, setRewrites] = useState([]);
  const [error, setError] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [theme, setTheme] = useState(getStoredTheme());

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const syncTheme = () => setTheme(localStorage.getItem(THEME_KEY) || "light");
    window.addEventListener("storage", syncTheme);
    window.addEventListener("hookora-preferences-updated", syncTheme);
    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("hookora-preferences-updated", syncTheme);
    };
  }, []);

  const isDark = theme === "dark";

  const colors = useMemo(
    () => ({
      cardBg: isDark ? "rgba(15,23,42,0.78)" : "#ffffff",
      cardBgStrong: isDark ? "rgba(15,23,42,0.92)" : "#ffffff",
      border: isDark ? "rgba(148,163,184,0.22)" : "#e5e7eb",
      borderStrong: isDark ? "rgba(148,163,184,0.34)" : "#d1d5db",
      text: isDark ? "#f8fafc" : "#111827",
      textMuted: isDark ? "#94a3b8" : "#6b7280",
      textSoft: isDark ? "#cbd5e1" : "#374151",
      inputBg: isDark ? "#0f172a" : "#ffffff",
      inputText: isDark ? "#f8fafc" : "#111827",
      trackBg: isDark ? "#1e293b" : "#e5e7eb",
      panelBg: isDark ? "rgba(30,41,59,0.55)" : "#f8fafc",
      buttonBg: isDark ? "#e2e8f0" : "#111827",
      buttonText: isDark ? "#020617" : "#ffffff",
      secondaryButtonBg: isDark ? "#1e293b" : "#e5e7eb",
      secondaryButtonText: isDark ? "#f8fafc" : "#111827",
      dangerBg: isDark ? "rgba(127,29,29,0.25)" : "#fef2f2",
      dangerText: isDark ? "#fecaca" : "#b91c1c",
      label: isDark ? "#e2e8f0" : "#111827",
      heroBg: isDark
        ? "linear-gradient(135deg, rgba(30,41,59,0.84), rgba(15,23,42,0.96))"
        : "linear-gradient(135deg, #f8fafc, #ffffff)",
      heroBorder: isDark ? "rgba(59,130,246,0.24)" : "#bfdbfe",
      heroBadgeBg: isDark ? "rgba(37,99,235,0.14)" : "#eff6ff",
      heroBadgeText: isDark ? "#bfdbfe" : "#1d4ed8",
      heroBadgeBorder: isDark ? "rgba(96,165,250,0.24)" : "#bfdbfe",
      shadow: isDark
        ? "0 16px 34px rgba(2,6,23,0.34)"
        : "0 12px 28px rgba(15,23,42,0.06)",
      shadowSoft: isDark
        ? "0 10px 24px rgba(2,6,23,0.22)"
        : "0 6px 18px rgba(15,23,42,0.05)",
    }),
    [isDark]
  );

  const inputStyle = {
    width: "100%",
    padding: "13px 14px",
    borderRadius: "12px",
    border: `1px solid ${colors.borderStrong}`,
    outline: "none",
    boxSizing: "border-box",
    background: colors.inputBg,
    color: colors.inputText,
    fontSize: "14px",
  };

  const textareaStyle = {
    ...inputStyle,
    minHeight: "100px",
    resize: "vertical",
  };

  const labelStyle = {
    display: "flex",
    alignItems: "center",
    marginBottom: "6px",
    color: colors.label,
    fontSize: "14px",
    fontWeight: 600,
  };

  const clearResults = () => {
    setRewrites([]);
    setCopiedId(null);
    setError("");
  };

  const resetForm = () => {
    setSharedTopic("");
    setSharedPlatform("YouTube");
    setOriginalDescription("");
    setAudience("");
    setGoal("");
    setOffer("");
    setPainPoint("");
    setDesiredOutcome("");
    setAngle("");
    setCallToAction("");
    setKeywords("");
    setBrandVoice("");
    setAvoidPhrases("");
    setExtraContext("");
    setRewrites([]);
    setCopiedId(null);
    setError("");
  };

  const generateRewrites = async () => {
    if (!sharedTopic.trim()) {
      setError(
        t("studioTools.descriptionTool.messages.topicRequired", "Please enter a topic first.")
      );
      return;
    }

    try {
      setLoading(true);
      setError("");

      const response = await API.post("/studio/descriptions", {
        topic: sharedTopic.trim(),
        platform: sharedPlatform,
        original_description: originalDescription,
        audience,
        goal,
        offer,
        pain_point: painPoint,
        desired_outcome: desiredOutcome,
        angle,
        call_to_action: callToAction,
        keywords,
        brand_voice: brandVoice,
        avoid_phrases: avoidPhrases,
        extra_context: extraContext,
        language: getPreferredLanguage(),
      });

      setRewrites(Array.isArray(response?.data?.rewrites) ? response.data.rewrites : []);
      setCopiedId(null);
      notifyStudioUsageUpdated();
    } catch (err) {
      console.error("Failed to generate rewrites:", err);
      setError(
        err?.response?.data?.detail ||
          t(
            "studioTools.descriptionTool.messages.generateFailed",
            "Failed to rewrite description."
          )
      );
    } finally {
      setLoading(false);
    }
  };

  const copyRewrite = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error("Failed to copy rewrite:", err);
    }
  };

  const renderScoreBar = (score) => {
    const safe = Number(score) || 0;
    const tone = getScoreTone(safe);
    return (
      <div
        style={{
          marginTop: "10px",
          height: "10px",
          borderRadius: "999px",
          background: colors.trackBg,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${Math.max(0, Math.min(100, safe))}%`,
            height: "100%",
            borderRadius: "999px",
            background: tone.bar,
          }}
        />
      </div>
    );
  };

  return (
    <MotionSurface>
    <div style={{ display: "grid", gap: "18px" }}>
      <div
        style={{
          border: `1px solid ${colors.heroBorder}`,
          borderRadius: "20px",
          padding: "18px",
          background: colors.heroBg,
          boxShadow: colors.shadow,
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            padding: "6px 10px",
            borderRadius: "999px",
            background: colors.heroBadgeBg,
            color: colors.heroBadgeText,
            border: `1px solid ${colors.heroBadgeBorder}`,
            fontSize: "12px",
            fontWeight: 800,
            letterSpacing: "0.03em",
            marginBottom: "12px",
          }}
        >
          {t("studioTools.descriptionTool.hero.badge", "Rewrite tool")}
        </div>

        <div style={{ color: colors.text, fontSize: "24px", fontWeight: 800, marginBottom: "8px" }}>
          {t("studioTools.descriptionTool.hero.title", "Descriptions")}
        </div>

        <div style={{ color: colors.textSoft, lineHeight: "1.7", maxWidth: "880px" }}>
          {t(
            "studioTools.descriptionTool.hero.description",
            "Use this tool to generate <strong>stronger description rewrites</strong> for your content. You can paste an existing description to improve it, or generate fresh direction from your topic and context."
          )}
        </div>
      </div>

      <div
        style={{
          border: `1px solid ${colors.border}`,
          borderRadius: "18px",
          padding: "18px",
          background: colors.cardBg,
          boxShadow: colors.shadowSoft,
        }}
      >
        <div style={{ display: "grid", gap: "14px" }}>
          <div>
            <label style={labelStyle}>
              {t("studioTools.descriptionTool.fields.topic.label", "Topic")}
              <FieldHint
                text={t(
                  "studioTools.descriptionTool.fields.topic.hint",
                  "The main subject of the description. Be clear about what the content or asset is about."
                )}
              />
            </label>
            <textarea
              value={sharedTopic}
              onChange={(e) => setSharedTopic(e.target.value)}
              placeholder={t(
                "studioTools.descriptionTool.fields.topic.placeholder",
                "What is the description about?"
              )}
              style={textareaStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>
              {t("studioTools.descriptionTool.fields.platform.label", "Platform")}
              <FieldHint
                text={t(
                  "studioTools.descriptionTool.fields.platform.hint",
                  "Choose the platform where this description will be used."
                )}
              />
            </label>
            <select
              value={sharedPlatform}
              onChange={(e) => setSharedPlatform(e.target.value)}
              style={inputStyle}
            >
              {["YouTube", "Instagram", "TikTok", "LinkedIn", "Facebook", "X"].map((platform) => (
                <option key={platform} value={platform}>
                  {platform}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={labelStyle}>
              {t("studioTools.descriptionTool.fields.originalDescription.label", "Original Description")}
              <FieldHint
                text={t(
                  "studioTools.descriptionTool.fields.originalDescription.hint",
                  "Paste the current description here if you want it improved instead of generating a fresh version from context alone."
                )}
              />
            </label>
            <textarea
              value={originalDescription}
              onChange={(e) => setOriginalDescription(e.target.value)}
              placeholder={t(
                "studioTools.descriptionTool.fields.originalDescription.placeholder",
                "Paste the current description if you want it rewritten."
              )}
              style={textareaStyle}
            />
          </div>

          <div
            style={{
              padding: "14px",
              borderRadius: "14px",
              background: colors.panelBg,
              border: `1px solid ${colors.border}`,
            }}
          >
            <div style={{ fontWeight: 700, color: colors.text, marginBottom: "12px" }}>
              {t("studioTools.descriptionTool.contextTitle", "Rewrite Context")}
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: "12px",
              }}
            >
              <div>
                <label style={labelStyle}>
                  {t("studioTools.descriptionTool.fields.audience.label", "Audience")}
                  <FieldHint
                    text={t(
                      "studioTools.descriptionTool.fields.audience.hint",
                      "Who this description is intended for. Example: students, creators, brand owners, startup founders."
                    )}
                  />
                </label>
                <input
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                  placeholder={t("studioTools.descriptionTool.fields.audience.placeholder", "Audience")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("studioTools.descriptionTool.fields.goal.label", "Goal")}
                  <FieldHint
                    text={t(
                      "studioTools.descriptionTool.fields.goal.hint",
                      "What the description should help with. Example: conversions, clarity, watch time, clicks, signups."
                    )}
                  />
                </label>
                <input
                  value={goal}
                  onChange={(e) => setGoal(e.target.value)}
                  placeholder={t("studioTools.descriptionTool.fields.goal.placeholder", "Goal")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("studioTools.descriptionTool.fields.offer.label", "Offer")}
                  <FieldHint
                    text={t(
                      "studioTools.descriptionTool.fields.offer.hint",
                      "The product, freebie, service, resource, or promise behind the content."
                    )}
                  />
                </label>
                <input
                  value={offer}
                  onChange={(e) => setOffer(e.target.value)}
                  placeholder={t("studioTools.descriptionTool.fields.offer.placeholder", "Offer or product")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("studioTools.descriptionTool.fields.painPoint.label", "Pain Point")}
                  <FieldHint
                    text={t(
                      "studioTools.descriptionTool.fields.painPoint.hint",
                      "The challenge or frustration your audience is facing."
                    )}
                  />
                </label>
                <input
                  value={painPoint}
                  onChange={(e) => setPainPoint(e.target.value)}
                  placeholder={t("studioTools.descriptionTool.fields.painPoint.placeholder", "Pain point")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("studioTools.descriptionTool.fields.desiredOutcome.label", "Desired Outcome")}
                  <FieldHint
                    text={t(
                      "studioTools.descriptionTool.fields.desiredOutcome.hint",
                      "The result your audience wants. Example: more growth, better content, faster results, less stress."
                    )}
                  />
                </label>
                <input
                  value={desiredOutcome}
                  onChange={(e) => setDesiredOutcome(e.target.value)}
                  placeholder={t(
                    "studioTools.descriptionTool.fields.desiredOutcome.placeholder",
                    "Desired outcome"
                  )}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("studioTools.descriptionTool.fields.angle.label", "Angle")}
                  <FieldHint
                    text={t(
                      "studioTools.descriptionTool.fields.angle.hint",
                      "The positioning or framing. Example: educational, premium, urgent, beginner-focused, direct-response."
                    )}
                  />
                </label>
                <input
                  value={angle}
                  onChange={(e) => setAngle(e.target.value)}
                  placeholder={t("studioTools.descriptionTool.fields.angle.placeholder", "Angle")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("studioTools.descriptionTool.fields.cta.label", "CTA")}
                  <FieldHint
                    text={t(
                      "studioTools.descriptionTool.fields.cta.hint",
                      "The action the description should encourage. Example: subscribe, watch, comment, save, visit the link."
                    )}
                  />
                </label>
                <input
                  value={callToAction}
                  onChange={(e) => setCallToAction(e.target.value)}
                  placeholder={t("studioTools.descriptionTool.fields.cta.placeholder", "CTA")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("studioTools.descriptionTool.fields.keywords.label", "Keywords")}
                  <FieldHint
                    text={t(
                      "studioTools.descriptionTool.fields.keywords.hint",
                      "Important search or SEO words to naturally include. Separate them with commas."
                    )}
                  />
                </label>
                <input
                  value={keywords}
                  onChange={(e) => setKeywords(e.target.value)}
                  placeholder={t("studioTools.descriptionTool.fields.keywords.placeholder", "Keywords")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("studioTools.descriptionTool.fields.brandVoice.label", "Brand Voice")}
                  <FieldHint
                    text={t(
                      "studioTools.descriptionTool.fields.brandVoice.hint",
                      "How the description should sound. Example: bold, expert, playful, premium, direct."
                    )}
                  />
                </label>
                <input
                  value={brandVoice}
                  onChange={(e) => setBrandVoice(e.target.value)}
                  placeholder={t("studioTools.descriptionTool.fields.brandVoice.placeholder", "Brand voice")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("studioTools.descriptionTool.fields.avoidPhrases.label", "Avoid Phrases")}
                  <FieldHint
                    text={t(
                      "studioTools.descriptionTool.fields.avoidPhrases.hint",
                      "Words or patterns you want the generator to avoid repeating."
                    )}
                  />
                </label>
                <input
                  value={avoidPhrases}
                  onChange={(e) => setAvoidPhrases(e.target.value)}
                  placeholder={t(
                    "studioTools.descriptionTool.fields.avoidPhrases.placeholder",
                    "Avoid phrases"
                  )}
                  style={inputStyle}
                />
              </div>
            </div>

            <div style={{ marginTop: "12px" }}>
              <label style={labelStyle}>
                {t("studioTools.descriptionTool.fields.extraContext.label", "Extra Context")}
                <FieldHint
                  text={t(
                    "studioTools.descriptionTool.fields.extraContext.hint",
                    "Add proof, supporting details, target emotion, campaign notes, or other extra context to improve the rewrite."
                  )}
                />
              </label>
              <textarea
                value={extraContext}
                onChange={(e) => setExtraContext(e.target.value)}
                placeholder={t(
                  "studioTools.descriptionTool.fields.extraContext.placeholder",
                  "Extra context, unique proof, product notes, or supporting detail"
                )}
                style={textareaStyle}
              />
            </div>
          </div>

          {error && (
            <div
              style={{
                padding: "12px 14px",
                borderRadius: "12px",
                background: colors.dangerBg,
                color: colors.dangerText,
              }}
            >
              {error}
            </div>
          )}

          <ActionRow>
            <AIActionButton isDark={isDark}
              onClick={generateRewrites}
              disabled={loading}
            >
              {loading
                ? t("studioTools.descriptionTool.actions.generating", "Generating...")
                : t("studioTools.descriptionTool.actions.generate", "Generate Rewrites")}
            </AIActionButton>

            <UtilityButton isDark={isDark}
              type="button"
              onClick={clearResults}
            >
              {t("studioTools.descriptionTool.actions.clearResults", "Clear Results")}
            </UtilityButton>

            <UtilityButton isDark={isDark}
              type="button"
              onClick={resetForm}
            >
              {t("studioTools.descriptionTool.actions.resetForm", "Reset Form")}
            </UtilityButton>
          </ActionRow>
        </div>
      </div>

      {!!rewrites.length && (
        <div
          style={{
            color: colors.textMuted,
            fontSize: "14px",
            lineHeight: "1.6",
            marginTop: "-4px",
          }}
        >
          {t(
            "studioTools.descriptionTool.messages.ready",
            "These are <strong>ready-to-use description rewrites</strong>. Pick the one that best matches your platform, clarity goal, and conversion direction."
          )}
        </div>
      )}

      <div style={{ display: "grid", gap: "14px" }}>
        {loading ? (
          <ToolResultSkeleton isDark={isDark} count={3} />
        ) : (
        rewrites.map((rewrite, index) => {
          const safeScore = Number(rewrite.score) || 0;
          const tone = getScoreTone(safeScore);

          return (
            <ResultCard
              key={`${rewrite.title}-${index}`}
              colors={colors}
              index={index}
            >
              <div style={{ color: colors.text, fontWeight: 700, fontSize: "18px" }}>
                {rewrite.title}
              </div>

              <div style={{ marginTop: "10px" }}>
                <span
                  style={{
                    background: tone.pillBg,
                    color: tone.pillText,
                    borderRadius: "999px",
                    padding: "6px 10px",
                    fontSize: "12px",
                    fontWeight: 700,
                  }}
                >
                  {t("studioTools.descriptionTool.result.score", "Score")}: {safeScore}
                </span>
              </div>

              <div
                style={{
                  whiteSpace: "pre-wrap",
                  color: colors.text,
                  lineHeight: "1.65",
                  marginTop: "12px",
                }}
              >
                {rewrite.text}
              </div>

              {renderScoreBar(safeScore)}

              <ActionRow style={{ marginTop: "14px" }}>
                <CopyButton
                  copied={copiedId === index}
                  onClick={() => copyRewrite(rewrite.text, index)}
                >
                  {copiedId === index
                    ? t("studioTools.descriptionTool.actions.copied", "Copied")
                    : t("studioTools.descriptionTool.actions.copy", "Copy")}
                </CopyButton>

                <SaveStudioAssetButton
                  toolType="descriptions"
                  title={`${rewrite.title} • ${sharedTopic || "Untitled Topic"}`}
                  topic={sharedTopic}
                  platform={sharedPlatform}
                  content={rewrite.text || ""}
                  meta={{ score: rewrite.score, label: rewrite.label }}
                />
              </ActionRow>
            </ResultCard>
          );
        })
        )}
      </div>
    </div>
    </MotionSurface>
  );
};

export default DescriptionTool;